import { useEffect, useState } from 'react';
import {
	useSupergridMutations, SuperGridMutatorMode, useSuperGridValidate,
} from '@aleph/supergrid';
import { SpotifyEntityCreateStatus, SpotifySuperGridMutationsConfig } from 'wb-spotify/app/routes/superGrid/mutator/utils';
import SpotifyHorizontalMutatorGrid from 'wb-spotify/app/routes/superGrid/mutator/horizontal/grid';
import SpotifyVerticalMutationGrid from 'wb-spotify/app/routes/superGrid/mutator/vertical/grid';
import { AnimatePresence, motion } from 'framer-motion';
import SpotifyMutatorActionRowContainer from 'wb-spotify/app/routes/superGrid/mutator/actionRow';
import { useUrlParams, CreateOverlayContainer, useCreateLoading } from '@aleph/shared-components';
import useConfirmSpotifyMutations from 'wb-spotify/app/routes/superGrid/mutator/hooks/useConfirmSpotifyMutations';
import { GridApi } from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';

const SpotifyMutatorSuperGrid = () => {
	const navigate = useNavigate();
	const { params, setUrlParam } = useUrlParams({
		navigate,
		useParams,
	});
	const { confirmMutations } = useConfirmSpotifyMutations();
	const { context: { setValidations } } = useSuperGridValidate();

	const [pendingCreationStatus, setPendingCreationStatus] = useState<undefined | SpotifyEntityCreateStatus>(undefined);

	const onConfirmMutations = (api: GridApi, status: SpotifyEntityCreateStatus = SpotifyEntityCreateStatus.ACTIVE) => {
		api.stopEditing();

		setTimeout(() => {
			setPendingCreationStatus(status);
		});
	};

	useEffect(() => {
		setValidations([]);

		return () => {
			setValidations([]);
		};
	}, []);

	useEffect(() => {
		if (pendingCreationStatus) {
			confirmMutations(pendingCreationStatus);
			setPendingCreationStatus(undefined);
		}
	}, [pendingCreationStatus]);

	const {
		hasMutations,
	} = useSupergridMutations<SpotifySuperGridMutationsConfig>();

	const { createLoading } = useCreateLoading();

	const mode = params?.mutatorMode ?? SuperGridMutatorMode.VERTICAL;
	const setMode = (newMode) => setUrlParam('mutatorMode', newMode);

	if (!hasMutations()) {
		return null;
	}

	return (
		<div
			className="flex flex-col flex-1"
			data-testid="supergrid-container"
		>
			{ createLoading && <CreateOverlayContainer /> }

			<SpotifyMutatorActionRowContainer
				selectedMode={mode}
				setSelectedMode={setMode}
			/>

			<AnimatePresence
				mode="wait"
			>
				{
					mode === SuperGridMutatorMode.HORIZONTAL ? <motion.div
						key="horizontal"
						className="flex flex-col flex-1"
						transition={{ duration: 0.1 }}
						initial={{ filter: 'blur(10px)' }}
						animate={{ filter: 'blur(0px)' }}
						exit={{ filter: 'blur(10px)' }}
						data-testid="horizontal-supergrid-container"
					>
						<SpotifyHorizontalMutatorGrid
							confirmMutations={onConfirmMutations}
						/>
					</motion.div> : <motion.div
						key="vertical"
						className="flex flex-col flex-1 mt-[16px]"
						transition={{ duration: 0.1 }}
						initial={{ filter: 'blur(10px)' }}
						animate={{ filter: 'blur(0px)' }}
						exit={{ filter: 'blur(10px)' }}
						data-testid="vertical-supergrid-container"
					>
						<SpotifyVerticalMutationGrid
							confirmMutations={onConfirmMutations}
						/>
					</motion.div>
				}
			</AnimatePresence>
		</div>
	);
};

export default SpotifyMutatorSuperGrid;
